<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CardLabels from 'dashboard/components-next/Conversation/ConversationCard/CardLabels.vue';

const props = defineProps({
  plan: {
    type: Object,
    required: true,
  },
  accountLabels: {
    type: Array,
    default: null,
  },
});

const emit = defineEmits(['delete']);

const { t } = useI18n();

let accountLabelsRef;
try {
  accountLabelsRef = useMapGetter('labels/getLabels');
} catch (e) {
  // Store não disponível (ex: em stories)
  accountLabelsRef = { value: [] };
}

const activeAccountLabels = computed(
  () => props.accountLabels || accountLabelsRef.value || []
);

// Converter IDs de audience para títulos de labels
const audienceLabelTitles = computed(() => {
  if (!props.plan.audience || props.plan.audience.length === 0) {
    return [];
  }
  
  const labelIds = props.plan.audience.map(a => a.id);
  return activeAccountLabels.value
    .filter(label => labelIds.includes(label.id))
    .map(label => label.title);
});

const hasAudienceLabels = computed(() => {
  return audienceLabelTitles.value.length > 0;
});

const formattedTemplateName = computed(() => {
  if (!props.plan.template_params?.name) return '-';
  return props.plan.template_params.name
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
});

const templatePreviewText = computed(() => {
  // Tentar extrair preview do template ou usar nome formatado
  return t('CAMPAIGN_PLANS.LIST.TEMPLATE_PREVIEW', { 
    template: formattedTemplateName.value 
  });
});

const responseMessageText = computed(() => {
  return props.plan.template_params?.response_message_text || null;
});

const responseMessageDisplay = computed(() => {
  return responseMessageText.value || '-';
});

const templateMessage = computed(() => {
  return props.plan.template_params?.rendered_template || '';
});

const hasTemplateMessage = computed(() => {
  return templateMessage.value && templateMessage.value.trim().length > 0;
});

const isCardExpanded = ref(false);
</script>

<template>
  <CardLayout
    layout="col"
    class="cursor-pointer transition-all hover:shadow-lg"
    @click="isCardExpanded = !isCardExpanded"
  >
    <!-- Linha Principal: Nome à Esquerda, Público e Ações à Direita -->
    <div class="flex flex-col w-full gap-4 min-[550px]:flex-row min-[550px]:items-center min-[550px]:justify-between">
      <!-- Nome do Plano -->
      <div class="flex items-center justify-between w-full gap-2 min-[550px]:w-auto min-[550px]:justify-start min-[550px]:flex-1 overflow-hidden">
        <span
          class="text-base font-semibold text-n-slate-12 truncate flex-1 min-w-0"
        >
          {{ plan.name }}
        </span>
      </div>

      <!-- Público-alvo e Ações -->
      <div class="flex items-center justify-start w-full gap-3 min-[550px]:w-auto min-[550px]:justify-end flex-shrink-0">
        <!-- Labels do Público -->
        <div v-if="hasAudienceLabels" class="flex justify-end flex-shrink-0">
          <CardLabels
            :conversation-labels="audienceLabelTitles"
            :account-labels="activeAccountLabels"
          />
        </div>

        <!-- Botão de deletar -->
        <div class="flex items-center gap-2" @click.stop>
          <Button
            variant="faded"
            color="ruby"
            size="sm"
            icon="i-lucide-trash"
            @click="emit('delete')"
          />
        </div>
      </div>
    </div>

    <!-- Conteúdo Expandível -->
    <template #after>
      <div
        class="grid transition-[grid-template-rows] duration-300 ease-in-out w-full"
        :class="isCardExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'"
      >
        <div class="overflow-hidden">
          <div class="flex flex-col gap-4 px-6 pb-5">
            <!-- Mensagem do Template -->
            <div v-if="hasTemplateMessage" class="text-sm text-n-slate-11 min-h-[2.5rem] whitespace-pre-wrap">
              {{ templateMessage }}
            </div>
            <div v-else class="text-sm text-n-slate-11 min-h-[2.5rem]">
              <span class="font-medium text-n-slate-12">{{ t('CAMPAIGN_PLANS.LIST.TEMPLATE') }}:</span>
              {{ formattedTemplateName }}
            </div>

            <!-- Volume Padrão (sempre visível) -->
            <div class="text-sm text-n-slate-11">
              <span class="font-medium text-n-slate-12">{{ t('CAMPAIGN_PLANS.LIST.DEFAULT_VOLUME') }}:</span>
              {{ plan.default_volume }} {{ t('CAMPAIGN_PLANS.LIST.CONTACTS') }}
            </div>

            <!-- Resposta automática (se houver) -->
            <div v-if="responseMessageText && responseMessageText.trim()" class="flex flex-col gap-3 w-full">
              <div class="flex flex-col gap-1.5 pl-3 border-l-2 border-n-strong">
                <div class="flex items-center gap-1.5 text-n-slate-10">
                  <span class="i-lucide-corner-down-right size-3.5" />
                  <span class="text-xs font-medium">
                    {{ t('CAMPAIGN_PLANS.LIST.RESPONSE_MESSAGE_LABEL') }}
                  </span>
                </div>
                <p class="text-sm text-n-slate-11 whitespace-pre-wrap">
                  {{ responseMessageDisplay }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </CardLayout>
</template>
